import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState } from 'state/store';
import { InsightCatalogue } from 'types/store/normalized/insights/InsightCatalogue';
import { BaseFilterDefinition } from 'types/store/normalized/insights/insightFilterMetadata';

/* ============================== STATE SETUP =============================== */
type InsightFilterDefinition = {
  id: string;
} & BaseFilterDefinition;

interface InsightFilterState {
  insightFilters: Partial<Record<InsightCatalogue, string[]>>;
}

const initialState: InsightFilterState = {
  insightFilters: {},
};

const insightFilterAdapter = createEntityAdapter<InsightFilterDefinition>();

/* ================================= REDUCER ================================ */
const insightFilterMetadataSlice = createSlice({
  name: 'normalized/insightFilterMetadata',
  initialState: insightFilterAdapter.getInitialState(initialState),
  reducers: {
    setInsightFilterMetadata: (
      state,
      action: PayloadAction<{
        insightId: InsightCatalogue;
        filters: Record<string, BaseFilterDefinition>;
      }>
    ) => {
      const { insightId, filters } = action.payload;
      const filterIds = Object.keys(filters);

      // TODO: move to a transformer
      const filterDefinitions: InsightFilterDefinition[] = Object.entries(
        filters
      ).map(([filterId, definition]) => ({
        id: filterId,
        ...definition,
      }));

      insightFilterAdapter.addMany(state, filterDefinitions);
      state.insightFilters[insightId] = filterIds;
    },
  },
});

/* =============================== SELECTORS ================================ */
const defaultSelectors = insightFilterAdapter.getSelectors<RootState>(
  (state) => state.normalized.insightFilterMetadata
);

const selectFiltersForInsight = (
  state: RootState,
  insightId: InsightCatalogue
): string[] => {
  return state.normalized.insightFilterMetadata.insightFilters[insightId] || [];
};

const selectFilterDefinitionsForInsight = (
  state: RootState,
  insightId: InsightCatalogue
): Record<string, InsightFilterDefinition> => {
  const insightFilters = selectFiltersForInsight(state, insightId);
  return insightFilters.reduce((acc, filterId) => {
    const filter = defaultSelectors.selectById(state, filterId);
    if (filter) {
      acc[filterId] = filter;
    }
    return acc;
  }, {} as Record<string, InsightFilterDefinition>);
};

/* ================================ EXPORTS ================================= */
export const insightFilterMetadataSelectors = {
  ...defaultSelectors,
  selectFiltersForInsight,
  selectFilterDefinitionsForInsight,
};

export const { setInsightFilterMetadata } = insightFilterMetadataSlice.actions;

export default insightFilterMetadataSlice.reducer;
